import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { IS_LOCAL_MODE, getCurrentUser, login as easyAuthLogin, logout as easyAuthLogout, loginWithApiKey, clearBypassSession, type UserInfo } from '../services/auth';
import type { User } from '@shared/types';
import React from 'react';

interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'member' | 'disable';
  accessAreas?: ('task-management' | 'pj-kpi-management' | 'ricoh-spaces')[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  unauthorized: boolean;
  disabled: boolean;
  user: AuthUser | null;
  login: () => Promise<void>;
  loginWithBypassToken: (email: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const LOCAL_MODE_VALUE: AuthContextValue = {
  isAuthenticated: true,
  unauthorized: false,
  disabled: false,
  user: {
    id: 'local-test-user',
    email: 'local@test.com',
    displayName: 'Local Test User',
    role: 'owner' as const,
    accessAreas: ['task-management', 'pj-kpi-management', 'ricoh-spaces'] as const,
  },
  login: async () => {
  },
  loginWithBypassToken: async () => {
  },
  logout: async () => {
  },
  getAccessToken: async () => null,
  refreshUser: async () => {
  },
  loading: false,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function RemoteAuthProvider({ children }: { children: React.ReactNode }) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const fetchDbUser = useCallback(async () => {
    try {
      const response = await fetch('/api/me');
      if (response.ok) {
        const registeredUser = await response.json();
        setDbUser(registeredUser);
        setUnauthorized(false);
      } else if (response.status === 401) {
        console.warn('[useAuth] Unauthorized: domain not allowed');
        setUnauthorized(true);
      } else {
        console.warn('[useAuth] Failed to fetch user:', response.status);
      }
    } catch (err) {
      console.error('[useAuth] Error fetching user:', err);
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const info = await getCurrentUser();
        setUserInfo(info);

        if (info) {
          await fetchDbUser();
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        setUserInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [fetchDbUser]);

  const login = useCallback(async () => {
    easyAuthLogin();
  }, []);

  const loginWithBypassToken = useCallback(async (email: string, apiKey: string) => {
    const session = await loginWithApiKey(email, apiKey);
    setUserInfo({
      userId: session.userId,
      userDetails: session.email,
      userRoles: ['authenticated'],
      identityProvider: 'apikey',
    });
    await fetchDbUser();
  }, [fetchDbUser]);

  const logout = useCallback(async () => {
    clearBypassSession();
    easyAuthLogout();
  }, []);

  const getAccessToken = useCallback(async () => {
    return null;
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchDbUser();
  }, [fetchDbUser]);

  const value: AuthContextValue = {
    isAuthenticated: !!userInfo,
    unauthorized,
    disabled: dbUser?.role === 'disable',
    user: userInfo && dbUser
      ? {
          id: dbUser.id,
          email: dbUser.email,
          displayName: dbUser.displayName,
          role: dbUser.role,
          accessAreas: dbUser.accessAreas,
        }
      : null,
    login,
    loginWithBypassToken,
    logout,
    getAccessToken,
    refreshUser,
    loading,
  };

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (IS_LOCAL_MODE) {
    return React.createElement(AuthContext.Provider, { value: LOCAL_MODE_VALUE }, children);
  }
  return React.createElement(RemoteAuthProvider, null, children);
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
